import type { Character } from '../core/character';
import type { Tile } from '../core/tile';
import type { Persistence } from '../persistence/Persistence';
import type { Renderer } from '../render/Renderer';
import {
  LATENT_ENCOUNTER_BASE_RATE,
  isSpawnClusterCell,
  pickLatentEnemy,
  safeZoneDistance,
  safeZoneScale,
} from './frontierRisk';

export interface TileLifecycleDeps {
  readonly persistence: Pick<Persistence, 'saveTileMutations' | 'saveCharacter'>;
  readonly renderer?: Pick<Renderer, 'updateHex'>;
  readonly now: () => number;
  /**
   * The world generation seed (HEX-93). When supplied, the latent-encounter
   * roll's safe-zone distance folds in frontier settlements so the latent rate
   * and enemy tier ease near a frontier town. Optional + additive: omit it for
   * the origin-only metric (pre-HEX-93 behaviour) — existing tests pass a
   * seedless lifecycle and stay unchanged.
   */
  readonly worldSeed?: number;
  /**
   * Live liberated-town registry (HEX-122 / ADR-0018 §4). When supplied, the
   * latent-roll safe-zone distance eases within `SETTLEMENT_EASE_RADIUS` of any
   * liberated town — a liberated town makes its surroundings safer, exactly like
   * the origin. Optional + additive: omit for the origin-only metric. A provider
   * (not a static list) so the App can mutate the set on liberation without
   * rebuilding the lifecycle.
   */
  readonly liberatedTowns?: () => readonly { q: number; r: number }[];
  /**
   * Source of randomness for the ground latent-encounter roll (HEX-40 /
   * ADR-0012 §5). Defaults to `Math.random` in production; tests inject a
   * seeded PRNG to assert rate bands and rarity distributions.
   */
  readonly random?: () => number;
}

/**
 * Lifecycle hooks fired by the movement layer when a character finishes
 * crossing a tile (HEX-9 → HEX-10 integration; living-world enrichment in
 * HEX-29; ground latent-encounter mutation in HEX-40; universal discovery
 * reveal in HEX-42).
 *
 * Responsibilities:
 *   - First-cross of any tile sets `discoveredBy` / `discoveredAt` (immutable)
 *     and increments the character's `discoveries` counter (ADR-0012 §7).
 *   - First-cross flips `state: unrevealed → revealed` for every tile type
 *     and notifies the renderer to repaint — post HEX-42 the `?` placeholder
 *     is universal, so the post-cross repaint is the moment the real type
 *     becomes visible to the player.
 *   - Every cross overwrites `lastTraveller` / `lastTravelledAt` and
 *     increments `tile.crossings` (ADR-0012 §5 footfall counter).
 *   - On `ground` crosses (when `latentRollFired === false` and the tile
 *     is not in the spawn-town cluster) the latent-encounter roll fires
 *     at `LATENT_ENCOUNTER_BASE_RATE × safeZoneScale(d)`. On hit, the
 *     tile mutates in place (type → encounter, `lifecycleState` reshaped,
 *     `uncoveredBy` / `uncoveredAt` recorded). On miss, `latentRollFired`
 *     is set so the roll never re-fires on this tile.
 *   - All writes are appended through the `Persistence` interface.
 *
 * Discoverer-immediate semantics (ADR-0012 §5 / HEX-40 lock): when ground
 * mutates into an encounter mid-cross, the same character who triggered
 * the mutation faces the encounter immediately on this cross — the
 * `app/App.ts` walk loop reads `tile.type` after `onCross` returns and
 * routes through `maybeStartCombat`. Rationale: an uncovering should
 * bite; consequence-free mutation reads as spooky-action and dulls the
 * boredom-fix the latent roll is meant to deliver.
 *
 * Discovery vs uncovering (ADR-0012 §7): `discoveredBy` fires on first-cross;
 * `uncoveredBy` fires when the latent roll mutates ground → encounter.
 */
export class TileLifecycle {
  private readonly random: () => number;

  constructor(private readonly deps: TileLifecycleDeps) {
    this.random = deps.random ?? Math.random;
  }

  async onCross(character: Character, tile: Tile): Promise<void> {
    const timestamp = this.deps.now();
    const wasUndiscovered = tile.metadata.discoveredBy === null;

    tile.metadata = {
      ...tile.metadata,
      ...(wasUndiscovered ? { discoveredBy: character.id, discoveredAt: timestamp } : {}),
      lastTraveller: character.id,
      lastTravelledAt: timestamp,
    };
    tile.crossings += 1;

    let willRepaint = wasUndiscovered;
    if (wasUndiscovered && tile.state === 'unrevealed') {
      tile.state = 'revealed';
    }

    // Ground latent-encounter roll (ADR-0012 §5 / HEX-40). One-shot per
    // tile; the spawn-town cluster is hard-zero so spawn stays safe.
    if (
      tile.type === 'ground' &&
      tile.lifecycleState !== null &&
      tile.lifecycleState.kind === 'ground' &&
      tile.lifecycleState.latentRollFired === false &&
      !isSpawnClusterCell(tile.q, tile.r)
    ) {
      const d = safeZoneDistance(tile.q, tile.r, this.deps.worldSeed, this.deps.liberatedTowns?.());
      const rate = LATENT_ENCOUNTER_BASE_RATE * safeZoneScale(d);
      if (this.random() < rate) {
        const enemy = pickLatentEnemy(this.random, d);
        tile.type = 'encounter';
        tile.lifecycleState = {
          kind: 'encounter',
          enemyId: enemy.id,
          slayerBuffStack: 0,
          resolvedAt: null,
        };
        tile.metadata = {
          ...tile.metadata,
          uncoveredBy: character.id,
          uncoveredAt: timestamp,
        };
        if (tile.state === 'unrevealed') tile.state = 'revealed';
        // Mutation changed the visible face — repaint even if the discovery
        // path already would have.
        willRepaint = true;
      } else {
        tile.lifecycleState = { ...tile.lifecycleState, latentRollFired: true };
      }
    }

    await this.deps.persistence.saveTileMutations(tile);

    if (wasUndiscovered) {
      character.discoveries += 1;
      await this.deps.persistence.saveCharacter(character);
    }

    if (willRepaint) {
      this.deps.renderer?.updateHex({ q: tile.q, r: tile.r }, tile);
    }
  }

  /**
   * Force-discover a tile for `character` without crossing it (HEX-42 spawn
   * cluster seeding). Idempotent: if the tile is already discovered (by any
   * character) this is a no-op. On a fresh discovery the call records the
   * discoverer + timestamp, flips `state` to `revealed`, bumps the
   * character's `discoveries` counter, persists, and repaints if a renderer
   * is wired. No crossings increment, no latent roll, no `lastTraveller`
   * touch — this is a vision-side reveal, not a footfall.
   */
  async seedDiscovery(character: Character, tile: Tile): Promise<void> {
    if (tile.metadata.discoveredBy !== null) return;
    const timestamp = this.deps.now();
    tile.metadata = {
      ...tile.metadata,
      discoveredBy: character.id,
      discoveredAt: timestamp,
    };
    if (tile.state === 'unrevealed') tile.state = 'revealed';
    character.discoveries += 1;
    await this.deps.persistence.saveTileMutations(tile);
    await this.deps.persistence.saveCharacter(character);
    this.deps.renderer?.updateHex({ q: tile.q, r: tile.r }, tile);
  }
}
